import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe, CalendarPlus, Loader2, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const PLAN_COLORS = {
  trial: "bg-amber-100 text-amber-800",
  base: "bg-blue-100 text-blue-800",
  pro: "bg-violet-100 text-violet-800",
};

export default function AziendePage() {
  const [companies, setCompanies] = useState(null);
  const [extending, setExtending] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/saas/companies");
      setCompanies(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
      setCompanies([]);
    }
  };

  useEffect(() => { load(); }, []);

  const extend = async (id, nome) => {
    setExtending(id);
    try {
      const { data } = await api.post(`/saas/companies/${id}/extend-trial`, { days: 14 });
      toast.success(`${nome}: esteso di 14 giorni (fino al ${data.nuova_scadenza.slice(0, 10)})`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore estensione");
    } finally {
      setExtending(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="aziende-page">
      <div className="flex items-center gap-2">
        <Globe className="h-6 w-6 text-[#0044FF]" />
        <h1 className="text-3xl font-heading font-black tracking-tight">Aziende clienti</h1>
      </div>
      <p className="text-sm text-slate-500">
        Le aziende registrate sulla piattaforma. Puoi regalare giorni extra di
        prova (o estendere l'abbonamento) con un click.
      </p>

      {companies === null && (
        <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
      )}
      {companies?.length === 0 && (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="aziende-empty">
          Nessuna azienda registrata finora. Condividi il link di registrazione:{" "}
          <b>{window.location.origin}/registrati</b>
        </Card>
      )}
      <div className="space-y-3">
        {companies?.map((c) => (
          <Card key={c.id} className="p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`azienda-row-${c.id}`}>
            <div className="min-w-0">
              <div className="font-heading font-bold flex items-center gap-2 flex-wrap">
                {c.nome}
                <Badge className={`${PLAN_COLORS[c.plan] || "bg-slate-100 text-slate-700"} uppercase text-[10px]`}>
                  {c.plan}
                </Badge>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
                <span>{c.owner_email}</span>
                <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email_usate_mese} email/mese</span>
                {c.scadenza && (
                  <span className={c.days_left === 0 ? "text-red-600 font-bold" : ""}>
                    scade il {c.scadenza.slice(0, 10)} ({c.days_left} gg)
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => extend(c.id, c.nome)}
              disabled={extending === c.id}
              data-testid={`extend-trial-${c.id}`}
              className="font-bold"
            >
              {extending === c.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <><CalendarPlus className="h-4 w-4 mr-1.5" /> +14 giorni</>
              )}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
